import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { VerificationModal } from './common';

const IMAGE_ICON = '/images/image.svg';
const SIGNATURE_ICON = '/images/signature.svg';
const DOCUMENT_ICON = '/images/document.svg';

const TOOL_TYPE = { image: 'image', signature: 'signature', document: 'document' };

function getToolType(tool) {
  const name = (tool?.identificationId || '').toLowerCase();
  if (name.includes('signature')) return TOOL_TYPE.signature;
  if (name.includes('image') || name.includes('crop')) return TOOL_TYPE.image;
  return TOOL_TYPE.document;
}

const TYPE_CONFIG = {
  [TOOL_TYPE.image]: { icon: IMAGE_ICON, label: 'Photo' },
  [TOOL_TYPE.signature]: { icon: SIGNATURE_ICON, label: 'Signature' },
  [TOOL_TYPE.document]: { icon: DOCUMENT_ICON, label: 'Document' },
};

function groupToolsByType(tools) {
  const counts = { [TOOL_TYPE.image]: 0, [TOOL_TYPE.signature]: 0, [TOOL_TYPE.document]: 0 };
  tools.forEach((tool) => {
    const type = getToolType(tool);
    counts[type] = (counts[type] || 0) + 1;
  });
  return Object.entries(counts)
    .filter(([, count]) => count > 0)
    .map(([type, count]) => ({ type, ...TYPE_CONFIG[type], count }));
}

function getTemplateId(template) {
  return template?.id ?? template?._id?.$oid ?? template?._id;
}

export default function TemplateCard({ template, onDelete }) {
  const title = template?.title ?? '';
  const description = template?.description ?? '';
  const instructions = template?.instructions ?? '';
  const requirements = Array.isArray(template?.requirements) ? template.requirements : [];
  const tools = Array.isArray(template?.tools) ? template.tools : [];
  const templateId = getTemplateId(template);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const toolsGrouped = useMemo(() => groupToolsByType(tools), [tools]);

  const handleDeleteClick = (e) => {
    e.preventDefault();
    if (!templateId || !onDelete) return;
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (!templateId || !onDelete) return;
    setDeleting(true);
    onDelete(templateId).finally(() => setDeleting(false));
  };

  return (
    <article className="card template-card h-100">
      <div className="card-body d-flex flex-column">
        <div className="d-flex align-items-start justify-content-between gap-2 mb-1">
          <h3 className="template-card__title mb-0">{title}</h3>
          {templateId && (
            <div className="d-flex align-items-center gap-1">
              <Link to={`/templates/${templateId}/edit`} className="btn btn-sm btn-outline-primary template-card__edit" title="Edit template">
                <i className="bi bi-pencil" />
              </Link>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger template-card__delete"
                title="Delete template"
                onClick={handleDeleteClick}
                disabled={deleting}
                aria-label="Delete template"
              >
                {deleting ? (
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                ) : (
                  <i className="bi bi-trash" />
                )}
              </button>
            </div>
          )}
        </div>
        {description && (
          <>
            <hr className="template-card__hr" />
            <p className="template-card__description text-muted small mb-0">{description}</p>
          </>
        )}
        {instructions && (
          <>
            <hr className="template-card__hr" />
            <p className="template-card__instructions small mb-0">{instructions}</p>
          </>
        )}
        {requirements.length > 0 && (
          <>
            <hr className="template-card__hr" />
            <div className="template-card__requirements">
              <span className="template-card__label">Requirements</span>
              <ul className="template-card__list">
                {requirements.map((req, i) => (
                  <li key={i}>{req}</li>
                ))}
              </ul>
            </div>
          </>
        )}
        {toolsGrouped.length > 0 && (
          <>
            <hr className="template-card__hr" />
            <div className="template-card__tools">
              <span className="template-card__label template-card__label--tools">Tools</span>
              <div className="template-card__tools-icons">
                {toolsGrouped.map(({ type, icon, label, count }) => (
                  <div key={type} className="template-card__tool-icon" title={`${label}${count > 1 ? ` — used ${count} times` : ''}`}>
                    <div className="template-card__tool-icon-wrap">
                      <img src={icon} alt="" aria-hidden />
                      <span className="template-card__tool-badge" aria-hidden>{count}</span>
                    </div>
                    <span className="template-card__tool-name">{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
      <VerificationModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        title="Delete template"
        message="Do you really want to delete this template ?"
        confirmLabel="Delete"
        variant="danger"
      />
    </article>
  );
}
